import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Toast } from "antd-mobile";
import { Message, useStores } from "../../../../../models";
import { formatPrice, genPrivacyChatID } from "../../../../../utils";
import { MessageTypes, TakeOrderStatus } from "../../../../../proto/nbchat-proto";
import {
  orderStatusText,
  paymentMethodTextMap,
  PayPasswordModal,
  PayTypeModal,
} from "../../../../Wallet";
import { ToastError } from "../../../../Toast";
import useNavigation from "../../../../../hooks/useNavigation";

const Wrap = styled.div`
  width: 280px;
  max-width: 80vw;
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid #e8e8e8;
`;

const Title = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 13px;
  color: #444;
`;

const Status = styled.div<{ $color?: string }>`
  margin-top: 8px;
  color: ${({ $color }) => $color || "#1c9cea"};
  font-size: 13px;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const OrderMessage: React.FC<{ message: Message }> = ({ message }) => {
  const { navigate } = useNavigation();
  const { userStore, orderStore, walletStore, chatStore, messageStore } =
    useStores();
  const order = (message.attach?.order ||
    message.attach?.take_order ||
    {}) as any;
  const status = order.status;
  const statusMeta = orderStatusText[status] || {
    text: "订单消息",
    color: "#1c9cea",
    description: "",
  };
  const isSeller = order.seller_id === userStore.currentUser?.id;
  const [pwdVisible, setPwdVisible] = useState(false);
  const [payTypeVisible, setPayTypeVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    null | "approve" | "settle" | "cancel"
  >(null);

  const basePayload = useMemo(
    () => ({
      trans_no: order.trans_no,
      last_message_id: message.message_id,
    }),
    [order.trans_no, message.message_id]
  );

  const runWithPwd = (action: typeof pendingAction) => {
    setPendingAction(action);
    setPwdVisible(true);
  };

  const onPwdOk = () => {
    if (pendingAction === "approve") {
      setPayTypeVisible(true);
      return;
    }
    if (pendingAction === "settle") {
      orderStore.publicTakeOrderSettle(basePayload);
      Toast.show({ content: "已确认发货" });
    }
    if (pendingAction === "cancel") {
      orderStore.publicTakeOrderCancel(basePayload);
      Toast.show({ content: "已取消" });
    }
    setPendingAction(null);
  };

  const goDisputeForm = () => {
    navigate("MyComplaintForm", {
      order_no: order.order_no || order.trans_no,
      message: {
        message_id: message.message_id,
        attach: {
          order: {
            trans_no: order.trans_no,
            order_no: order.order_no,
          },
        },
      },
    });
  };

  const uploadAndPay = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
      try {
        // 先本地占位；实际上传沿用项目既有图片上传可后续接
        const imgs = files.map((f) => ({
          url: URL.createObjectURL(f),
          name: f.name,
        }));
        orderStore.publicTakeOrderPay({ ...basePayload, imgs } as any);
        Toast.show({ content: "凭证已提交" });
      } catch (e: any) {
        ToastError(e?.message || "上传失败");
      }
    };
    input.click();
  };

  const contactCs = () => {
    orderStore.onContactCs((user) => {
      const { chat_id } = genPrivacyChatID(userStore.currentUser.id, user.id);
      const chat = chatStore.chats.get(chat_id);
      if (chat) {
        chatStore.setCurrent(chat);
        navigate("Chat");
      }
    }, ToastError);
  };

  const remind = () => {
    const chat = chatStore.current;
    if (!chat) return;
    const msg = chat.newMessage({
      content: "有尚未处理的订单,麻烦请确认",
      type: MessageTypes.TEXT,
      mention_ids: [],
      reply_msg_id: null,
    });
    messageStore.sendMessageByMessageType(msg);
  };

  const actions: React.ReactNode[] = [];

  if (status === TakeOrderStatus.PENDING) {
    if (isSeller) {
      actions.push(
        <Button
          key="approve"
          size="mini"
          color="primary"
          onClick={() => {
            walletStore.loadPaymentList().then(() => runWithPwd("approve"));
          }}
        >
          确认订单
        </Button>
      );
      actions.push(
        <Button key="scancel" size="mini" onClick={() => runWithPwd("cancel")}>
          取消
        </Button>
      );
    } else {
      actions.push(
        <Button key="bcancel" size="mini" onClick={() => runWithPwd("cancel")}>
          取消订单
        </Button>
      );
      actions.push(
        <Button key="remind" size="mini" fill="outline" onClick={remind}>
          提醒卖家
        </Button>
      );
    }
  }

  if (status === TakeOrderStatus.MATCHING && !isSeller) {
    actions.push(
      <Button key="pay" size="mini" color="primary" onClick={uploadAndPay}>
        上传凭证并付款
      </Button>
    );
    actions.push(
      <Button key="bcancel2" size="mini" onClick={() => runWithPwd("cancel")}>
        取消
      </Button>
    );
  }

  if (status === TakeOrderStatus.DELIVERING && isSeller && !order.settled) {
    actions.push(
      <Button
        key="settle"
        size="mini"
        color="primary"
        onClick={() => runWithPwd("settle")}
      >
        确认发货
      </Button>
    );
    actions.push(
      <Button key="dispute" size="mini" onClick={goDisputeForm}>
        异常订单
      </Button>
    );
  }

  actions.push(
    <Button key="cs" size="mini" fill="outline" onClick={contactCs}>
      联系客服
    </Button>
  );

  return (
    <Wrap>
      <Title>{isSeller ? "卖出订单" : "买入订单"}</Title>
      <Row>
        <span>金额</span>
        <strong>
          {formatPrice(
            Number(order.quantities || 0) * Number(order.amount || 0)
          )}{" "}
          CNY
        </strong>
      </Row>
      <Row>
        <span>数量</span>
        <span>{order.quantities ?? "-"} 喜鹊币</span>
      </Row>
      <Row>
        <span>单价</span>
        <span>{formatPrice(order.amount || 0)}</span>
      </Row>
      <Row>
        <span>支付</span>
        <span>
          {paymentMethodTextMap[order.payment_method] ||
            order.payment_method ||
            "-"}
        </span>
      </Row>
      <Row>
        <span>单号</span>
        <span style={{ maxWidth: 160, wordBreak: "break-all" }}>
          {order.trans_no || order.order_no || "-"}
        </span>
      </Row>
      <Status $color={statusMeta.color}>{statusMeta.text}</Status>
      {statusMeta.description ? (
        <div style={{ marginTop: 6, color: "#666", fontSize: 12 }}>
          {statusMeta.description}
        </div>
      ) : null}
      <Actions>{actions}</Actions>

      <PayPasswordModal
        visible={pwdVisible}
        setVisible={setPwdVisible}
        callBack={(ok) => ok && onPwdOk()}
      />
      <PayTypeModal
        visible={payTypeVisible}
        setVisible={setPayTypeVisible}
        title="选择收款方式"
        paymentMethods={
          order.payment_methods ||
          (order.payment_method != null ? [order.payment_method] : [])
        }
        callBack={(payment) => {
          orderStore.publicTakeOrderApprove({
            ...basePayload,
            payment_id: payment.id,
          } as any);
          Toast.show({ content: "已确认订单" });
          setPendingAction(null);
        }}
      />
    </Wrap>
  );
};

export default observer(OrderMessage);
